import { DateTime } from 'luxon';
import queryString from 'query-string';
import callApi, { CallApiResult, getCustomerId } from 'src/api';
import { RegionKeys } from 'src/core/provider/AppMeta';
import { PageableQueryParams } from 'src/entities/basic-types/Pageable';
import { PersonArch } from 'src/entities/organization/Person';
import {
    SCHEMA_SUCCESS_LIST_APPOINTMENT_ADD_SHAPE,
    SCHEMA_SUCCESS_LIST_CALL_ADD_SHAPE,
    SCHEMA_SUCCESS_LIST_CONTROL_SHAPE,
    SCHEMA_SUCCESS_LIST_ENTRY_SHAPE,
    SCHEMA_SUCCESS_LIST_NOTE_ADD_SHAPE,
} from 'src/schemas/SuccessList';

/* Fetch Success List Overview */

export const fetchSuccessListOverview = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/namelist/summary';
    return callApi(endpoint);
};

/* Fetch Success List Topics */

export const fetchSuccessListTopics = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/namelistTopics';
    return callApi(endpoint);
};

/* Fetch Success List */

export const fetchSuccessList = (
    topic: string,
    pageable: PageableQueryParams = {},
    personArch?: PersonArch
): Promise<CallApiResult> => {
    let query = queryString.stringify({ ...pageable, suggestion: personArch }, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;

    const customerId = getCustomerId();
    const endpoint = `/api/partner/${customerId}/namelist/${topic}${query}`;

    return callApi(endpoint, { method: 'GET' });
};

/* Add Note */

export const successListNoteAdd = (data: SCHEMA_SUCCESS_LIST_NOTE_ADD_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/note';

    return callApi(endpoint, { method: 'POST', data });
};

/* Add Call */

export const successListCallAdd = (raw: SCHEMA_SUCCESS_LIST_CALL_ADD_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/call';

    return callApi(endpoint, {
        method: 'POST',
        data: {
            ...raw,
            followUpAppointmentDate:
                raw.followUpAppointmentDate instanceof Date
                    ? DateTime.fromJSDate(raw.followUpAppointmentDate).toISO()
                    : undefined,
        },
    });
};

/* Control */

export const successListControl = (raw: SCHEMA_SUCCESS_LIST_CONTROL_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/control';

    return callApi(endpoint, {
        method: 'POST',
        data: {
            ...raw,
            registeredCandidateId: Number(raw.registeredCandidateId),
            followUpAppointmentDate:
                raw.followUpAppointmentDate instanceof Date
                    ? DateTime.fromJSDate(raw.followUpAppointmentDate).toISO()
                    : undefined,
        },
    });
};

/* Fetch Direct Customers */

export const fetchDirectCustomers = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/direct-customers';

    return callApi(endpoint, { method: 'GET' });
};

/* Add / Edit Success */

export const successListEntry = (raw: SCHEMA_SUCCESS_LIST_ENTRY_SHAPE, patch?: boolean): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/nameListEntry';

    return callApi(endpoint, {
        method: patch ? 'PUT' : 'POST',
        data: {
            ...raw,
            dateOfBirth: raw.dateOfBirth instanceof Date ? DateTime.fromJSDate(raw.dateOfBirth).toISODate() : undefined,
            email: raw.email !== '' ? raw.email : undefined,
            address: raw.address !== '' ? raw.address : undefined,
            phoneNumber: raw.phoneNumber && raw.phoneNumber.length > 4 ? raw.phoneNumber : undefined,
            familiarity: Number(raw.familiarity),
        },
    });
};

/* Add Appointment */

export const successListAppointmentAdd = (raw: SCHEMA_SUCCESS_LIST_APPOINTMENT_ADD_SHAPE): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/appointment';

    return callApi(endpoint, {
        method: 'POST',
        data: {
            ...raw,
            isOnline: raw.isOnline === 'true',
            followUpAppointmentDate:
                raw.followUpAppointmentDate instanceof Date
                    ? DateTime.fromJSDate(raw.followUpAppointmentDate).toISO()
                    : undefined,
        },
    });
};

/* History */

export const fetchSuccessListEntryHistory = (nameListEntryId: string) => {
    const customerId = getCustomerId();
    const endpoint = `/api/partner/${customerId}/nameListEntry/${nameListEntryId}/history`;

    return callApi(endpoint, { method: 'GET' });
};

/* Fetch Success List Regions */

export const fetchSuccessListRegions = (): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/namelistCountries';
    return callApi(endpoint, { method: 'GET' });
};

/* Set Success List Regions */

export const submitSelectSuccessListRegions = (data: RegionKeys[]): Promise<CallApiResult> => {
    const customerId = getCustomerId();
    const endpoint = '/api/partner/' + customerId + '/namelistCountries';
    return callApi(endpoint, { method: 'PUT', data });
};
